import React, { useState, useEffect, useCallback } from 'react';
import { getViewStats, getViews, ViewStats, View } from '../services/viewTracking';
import AnimatedSection from './AnimatedSection';
import { useAuth } from '../context/AuthContext';

const AdminDashboard: React.FC = () => {
  const { admin } = useAuth();
  const [stats, setStats] = useState<ViewStats | null>(null);
  const [views, setViews] = useState<View[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, pages: 1 });

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getViewStats(dateRange);
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const loadViews = useCallback(async () => {
    try {
      const response = await getViews({
        ...dateRange,
        page: currentPage,
        limit: 50,
      });
      setViews(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load views');
    }
  }, [dateRange, currentPage]);

  useEffect(() => {
    loadStats();
    loadViews();
  }, [loadStats, loadViews]);

  const handleDateRangeChange = (startDate?: string, endDate?: string) => {
    setDateRange({ startDate, endDate });
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-dark-600 dark:text-neutral-300">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <section className="py-10 md:py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <h1 className="font-display text-2xl md:text-3xl font-semibold text-white mb-2">
              Dashboard
            </h1>
            <p className="text-muted text-sm">
              Welcome back, {admin?.name || admin?.email}.
            </p>
          </div>
        </AnimatedSection>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-600 dark:text-red-400 rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {/* Date Range Filter */}
        <AnimatedSection animation="fade-in" delay={100}>
          <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20 mb-8">
            <h3 className="text-lg font-semibold text-dark-900 dark:text-neutral-50 mb-4">Date Range Filter</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-600 dark:text-neutral-300 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateRange.startDate || ''}
                  onChange={(e) => handleDateRangeChange(e.target.value || undefined, dateRange.endDate)}
                  className="w-full px-4 py-2 rounded-lg border border-primary-500/20 bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50 focus:outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-600 dark:text-neutral-300 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateRange.endDate || ''}
                  onChange={(e) => handleDateRangeChange(dateRange.startDate, e.target.value || undefined)}
                  className="w-full px-4 py-2 rounded-lg border border-primary-500/20 bg-white dark:bg-dark-900 text-dark-900 dark:text-neutral-50 focus:outline-none focus:border-primary-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => handleDateRangeChange(undefined, undefined)}
                  className="w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors duration-300"
                >
                  Clear Filter
                </button>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Stats Overview */}
        {stats && (
          <AnimatedSection animation="fade-in" delay={200}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Views */}
              <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-dark-600 dark:text-neutral-400">Total Views</h3>
                  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-dark-900 dark:text-neutral-50">{stats.totalViews.toLocaleString()}</p>
              </div>

              {/* Unique Visitors */}
              <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-dark-600 dark:text-neutral-400">Unique Visitors</h3>
                  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-dark-900 dark:text-neutral-50">{stats.uniqueVisitors.toLocaleString()}</p>
              </div>

              {/* Average Views per Visitor */}
              <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-dark-600 dark:text-neutral-400">Avg. Views/Visitor</h3>
                  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-dark-900 dark:text-neutral-50">
                  {stats.uniqueVisitors > 0 ? (stats.totalViews / stats.uniqueVisitors).toFixed(1) : '0'}
                </p>
              </div>

              {/* Views Today */}
              <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-dark-600 dark:text-neutral-400">Views Today</h3>
                  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-3xl font-bold text-dark-900 dark:text-neutral-50">
                  {views.filter(v => {
                    const viewDate = new Date(v.viewedAt);
                    const today = new Date();
                    return viewDate.toDateString() === today.toDateString();
                  }).length}
                </p>
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* Views by Type */}
        {stats && stats.viewsByType.length > 0 && (
          <AnimatedSection animation="fade-in" delay={300}>
            <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20 mb-8">
              <h3 className="text-xl font-bold text-dark-900 dark:text-neutral-50 mb-6">Views by Type</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.viewsByType.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-primary-500/5 rounded-lg">
                    <div>
                      <p className="font-semibold text-dark-900 dark:text-neutral-50 capitalize">{item.viewType}</p>
                      <p className="text-sm text-dark-600 dark:text-neutral-400">{item.count.toLocaleString()} views</p>
                    </div>
                    <div className="text-2xl font-bold text-primary-500">{item.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* Top Resources */}
        {stats && stats.topResources.length > 0 && (
          <AnimatedSection animation="fade-in" delay={400}>
            <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20 mb-8">
              <h3 className="text-xl font-bold text-dark-900 dark:text-neutral-50 mb-6">Top Resources</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-primary-500/20">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Resource ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Type</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topResources.map((resource, index) => (
                      <tr key={index} className="border-b border-primary-500/10 hover:bg-primary-500/5 transition-colors">
                        <td className="py-3 px-4 text-sm text-dark-600 dark:text-neutral-300">{resource.resourceId}</td>
                        <td className="py-3 px-4 text-sm text-dark-600 dark:text-neutral-300 capitalize">{resource.viewType}</td>
                        <td className="py-3 px-4 text-sm text-right font-semibold text-primary-500">{resource.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* Recent Views */}
        <AnimatedSection animation="fade-in" delay={500}>
          <div className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-6 border border-primary-500/20">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-dark-900 dark:text-neutral-50">Recent Views</h3>
              <div className="text-sm text-dark-600 dark:text-neutral-400">
                Showing {views.length} of {pagination.total} views
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-primary-500/20">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Resource ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">IP Address</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-dark-900 dark:text-neutral-50">Viewed At</th>
                  </tr>
                </thead>
                <tbody>
                  {views.map((view) => (
                    <tr key={view._id} className="border-b border-primary-500/10 hover:bg-primary-500/5 transition-colors">
                      <td className="py-3 px-4 text-sm text-dark-600 dark:text-neutral-300 capitalize">{view.viewType}</td>
                      <td className="py-3 px-4 text-sm text-dark-600 dark:text-neutral-300">{view.resourceId || '-'}</td>
                      <td className="py-3 px-4 text-sm text-dark-600 dark:text-neutral-300 font-mono">{view.ipAddress}</td>
                      <td className="py-3 px-4 text-sm text-dark-600 dark:text-neutral-300">{formatDate(view.viewedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-dark-600 dark:text-neutral-400">
                  Page {currentPage} of {pagination.pages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={currentPage === pagination.pages}
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default AdminDashboard;

